/** Helpers compartilhados pelos configs de cadastro (opções + filtro por categoria). */
import { useState } from 'react'
import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import { CATALOG_STALE_TIME } from '../../../core/query/queryClient'
import type { Resource } from '../../../core/api/resource'
import type { Categoria } from '../../../core/api/types'
import type { Option } from '../../../shared/form/fields'
import type { ResourceFilters } from '../config'
import type { ListParams } from '../../../core/api/resource'
import { useDeclarativeText } from '../../../shared/i18n/declarativeText'
import { useI18n } from '../../../core/i18n/useI18n'
import { categoryLabel } from '../../../shared/categoryLabels'

/** Opções {value:code,label} de uma tabela de categoria (cache longo — catálogo). */
export function useCategoryOptions(resource: Resource<Categoria>): Option[] {
  const { t } = useI18n()
  const q = resource.useList({ per_page: 100 }, { staleTime: CATALOG_STALE_TIME })
  return (q.data?.items ?? []).map((c) => ({ value: c.code, label: categoryLabel(c, t) }))
}

/** Cria um hook de filtro por categoria (ToggleButtonGroup + param `category`). */
export function makeCategoryFilter(resource: Resource<Categoria>) {
  return function useCategoryFilter(): ResourceFilters {
    const [category, setCategory] = useState('')
    const tt = useDeclarativeText()
    const options = useCategoryOptions(resource)
    return {
      params: { category: category || undefined },
      node: (
        <ToggleButtonGroup
          size="small"
          exclusive
          value={category}
          onChange={(_, v) => setCategory(v ?? '')}
          aria-label={tt('Filtrar por categoria')}
          // A lista vem do banco e pode crescer quando o Protheus publica uma nova
          // Modalidade. Quebrar em mais linhas mantém todas as opções visíveis.
          sx={{
            flexWrap: 'wrap',
            // Sem isto o rótulo de duas palavras quebra dentro do próprio botão
            // ("Frota Leve") e desalinha a altura da fileira.
            '& .MuiToggleButton-root': { whiteSpace: 'nowrap' },
          }}
        >
          <ToggleButton value="">{tt('Todas')}</ToggleButton>
          {options.map((o) => (
            <ToggleButton key={o.value} value={o.value}>
              {o.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      ),
    }
  }
}


/** Situação de RH, como o backend a deriva em `personnel.situacao_colaborador`.
 *
 *  Os cinco códigos, e não os quatro que a operação costuma citar: "Bloqueado" é a
 *  defesa do HUB para admissão futura e cadastro sem origem, e esconder a opção
 *  esconderia justamente a linha que alguém precisa ir olhar. */
export const SITUACOES_COLABORADOR = [
  { value: 'ativo', label: 'Ativo' },
  { value: 'ferias', label: 'Férias' },
  { value: 'afastado', label: 'Afastado' },
  { value: 'demitido', label: 'Demitido' },
  { value: 'bloqueado', label: 'Bloqueado' },
] as const

/** A linha SEM pessoa, que a grade mostra como traço na coluna Situação. Não é uma
 *  situação de RH, é a ausência de uma, e por isso mora fora da lista acima. É com
 *  ela que a operação conta quantos conjuntos estão descobertos. O mesmo valor é
 *  reconhecido pelo servidor, em `app/utils/filtros.py`. */
export const SEM_PESSOA = 'sem_pessoa'

/** O traço da coluna Modalidade: conjunto sem cavalo, ou com cavalo que ainda não
 *  tem tipo. Mesmo valor reconhecido pelo servidor, em `app/utils/filtros.py`. */
export const SEM_MODALIDADE = 'sem_modalidade'

/** Filtro de situação da PESSOA do conjunto, por parâmetro de consulta.
 *
 *  Select, e não fileira de botões como a modalidade: são cinco rótulos longos, e
 *  em fileira eles empurrariam a modalidade para a linha de baixo em tela de
 *  notebook. Devolve o hook pronto para `useFilters`, e `compor` junta com outro
 *  quando a grade tem os dois.
 *
 *  `rotuloSemPessoa` é obrigatório porque a palavra muda com a grade, motorista no
 *  caminhão e operador na máquina, e porque nenhuma grade nova deve nascer sem
 *  conseguir responder quantas linhas estão sem ninguém. */
export function makeSituacaoFilter(param: string, rotulo: string, rotuloSemPessoa: string) {
  return function useSituacaoFilter(): ResourceFilters {
    const [situacao, setSituacao] = useState('')
    const tt = useDeclarativeText()
    return {
      params: { [param]: situacao || undefined },
      node: (
        <TextField
          select
          size="small"
          label={tt(rotulo)}
          value={situacao}
          onChange={(e) => setSituacao(e.target.value)}
          sx={{ minWidth: 190 }}
        >
          <MenuItem value="">{tt('Todas')}</MenuItem>
          {SITUACOES_COLABORADOR.map((o) => (
            <MenuItem key={o.value} value={o.value}>{tt(o.label)}</MenuItem>
          ))}
          <MenuItem value={SEM_PESSOA}>{tt(rotuloSemPessoa)}</MenuItem>
        </TextField>
      ),
    }
  }
}

/** Junta dois hooks de filtro numa faixa só, preservando a ordem declarada. */
export function comporFiltros(...hooks: Array<() => ResourceFilters>) {
  return function useFiltrosCompostos(): ResourceFilters {
    const partes = hooks.map((usar) => usar())
    return {
      params: partes.reduce<ListParams>((acc, parte) => ({ ...acc, ...(parte.params ?? {}) }), {}),
      node: (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {partes.map((parte, i) => <Box key={i}>{parte.node}</Box>)}
        </Box>
      ),
    }
  }
}
